import React, { useMemo, useState } from 'react'
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
} from '@tanstack/react-query'
import { apiGet } from '~/util/api'
import { TabNav, ButtonContainer, TabButton } from '~/components/shared/TabNav'
import Notification from './Notification'
import { Tabs, TabsList, TabsTrigger } from '~/lib/ui/tabs'
import useTranslation, {
  TranslationProvider,
} from '~/components/shared/TranslationProvider'
import * as translations from './locales.json'

export const queryClient = new QueryClient()

const Notifications = ({ locale, ...props }) => {
  return (
    <QueryClientProvider client={queryClient}>
      <TranslationProvider
        locale={locale}
        translations={translations}
        scope={'javascript.components.Notifications'}
      >
        <NotificationsInner {...props} />
      </TranslationProvider>
    </QueryClientProvider>
  )
}

const NotificationsInner = ({
  current_user,
  messages_path = '/coach/messages',
  accent_color = 'green',
}) => {
  const path = useMemo(() => `${window.location.pathname}.json`, [])
  const { t } = useTranslation('index')
  const accentClass =
    accent_color === 'blue' ? 'bg-[#08467C]' : 'bg-intoo-green-coachcta'
  const headingClass =
    accent_color === 'blue'
      ? 'mt-4 !text-[#08467C]'
      : 'mt-4 !text-intoo-green-coachcta'
  const pageSize = 10
  const [pageIndex, setPageIndex] = useState(0)
  const [activeTab, setActiveTab] = useState('inbox')
  const viewingArchived = activeTab === 'archived'

  const queryFn = async () => {
    try {
      const page = pageIndex + 1
      const res = await apiGet(path, {
        params: { page, per_page: pageSize, tab: activeTab },
      })
      return res.data
    } catch (error) {
      console.error('Error fetching data:', error)
      throw new Error('Error fetching data')
    }
  }

  const { data } = useQuery({
    queryKey: ['notifications', path, activeTab, pageIndex, pageSize],
    queryFn,
  })

  const { notifications, archived_notifications, pagination, counts } =
    data || {}

  const onChangeActiveTab = (activeTab) => {
    if (activeTab === 'messages') {
      window.location.href = messages_path
    }
  }

  const handleTabChange = (value) => {
    setActiveTab(value)
    setPageIndex(0)
  }

  const notificationsToDisplayRaw = viewingArchived
    ? archived_notifications || []
    : notifications || []

  const pageCount = pagination?.total_pages || 0
  const notificationsToDisplay = notificationsToDisplayRaw

  return (
    <div className="m-4 flex flex-col md:mx-9">
      <div className="mt-4">
        <h1 className={headingClass}>{t('notifications')}</h1>
        <p className="mb-4">{t('notifications_instructions_1')}</p>
        <p className="mb-4">{t('notifications_instructions_2')}</p>
        <p className="mb-4">{t('notifications_instructions_3')}</p>
        <TabNav
          defaultActiveTab="reminders"
          mobileMode="swipe"
          className="mb-4 md:flex md:flex-row md:justify-between md:border-b md:border-solid md:border-[#979797]"
          onChangeActiveTab={onChangeActiveTab}
        >
          <ButtonContainer
            className="justify-start md:border-none"
            carouselWrapperClass="min-h-[4.5rem]"
          >
            <TabButton
              name="messages"
              text="Messages"
              activeDecorationClass={accentClass}
            />
            <TabButton
              name="reminders"
              text={'Notifications'}
              activeDecorationClass={accentClass}
            />
          </ButtonContainer>
        </TabNav>
      </div>
      <Tabs className="Tabs" value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="mb-4">
          <TabsTrigger value="inbox">
            <div className="flex items-center gap-2">
              <span>Notifications</span>
              {(counts?.notifications || 0) > 0 && (
                <span className="text-[#D82727]">{`(${counts.notifications})`}</span>
              )}
            </div>
          </TabsTrigger>
          <TabsTrigger value="archived">Archived Notifications</TabsTrigger>
        </TabsList>
      </Tabs>
      {notificationsToDisplay.length === 0 && (
        <div className="text-center text-gray-500">
          {viewingArchived
            ? 'No archived notifications found.'
            : 'No current notifications found.'}
        </div>
      )}
      {notificationsToDisplay.length > 0 && (
        <NotificationCard
          notifications={notificationsToDisplay}
          current_user={current_user}
          archived={viewingArchived}
        />
      )}
      {pageCount > 1 && (
        <div className="flex items-center justify-center gap-8 p-4">
          <button
            className="rounded border px-3 py-1 disabled:opacity-50"
            onClick={() => setPageIndex((prev) => Math.max(prev - 1, 0))}
            disabled={pageIndex === 0 || !pagination?.prev_page}
          >
            Previous
          </button>
          <span>
            Page {pagination?.page || pageIndex + 1} of {pageCount}
          </span>
          <button
            className="rounded border px-3 py-1 disabled:opacity-50"
            onClick={() =>
              setPageIndex((prev) => Math.min(prev + 1, pageCount - 1))
            }
            disabled={pageIndex >= pageCount - 1 || !pagination?.next_page}
          >
            Next
          </button>
        </div>
      )}
    </div>
  )
}
export default Notifications

const NotificationCard = ({
  notifications,
  current_user,
  archived = false,
}) => {
  return (
    <div className="rounded-2.5xl border border-[#C0E2FF] bg-[#F7FCFF] px-4 py-7 md:px-8">
      <div className="flex flex-col">
        <div className="mb-8 max-w-[13rem]"></div>
      </div>
      <div className="flex flex-col gap-y-6">
        {notifications?.map((notification, _index) => (
          <Notification
            key={notification.id}
            notification={notification}
            current_user={current_user}
            archived={archived}
          />
        ))}
      </div>
    </div>
  )
}
